import { FC, useState } from "react";
import { SurveyAnswerType } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { Field, Radio, RadioGroup, Rating, Textarea, Checkbox } from "@fluentui/react-components";

export interface SurveyAnswerViewProps {
    id: string;
    questionNumber: number;
    question: string;
    answerType: SurveyAnswerType;
    options?: string[];
    onAnswer: (id: string, answer: string, type: SurveyAnswerType) => void;
}

// survey structure
const SurveyAnswerView: FC<SurveyAnswerViewProps> = ({ id, questionNumber, question, answerType, options, onAnswer }: SurveyAnswerViewProps) => {
    switch (answerType) {
        // plain-text question
        case SurveyAnswerType.Text:
            return <Field label={`${questionNumber}) ${question}`}>
                <Textarea size="large" onChange={(ev) => onAnswer(id, ev.target.value, answerType)} />
            </Field>;
        // single-value question (radio button)
        case SurveyAnswerType.SingleValue:
            return <Field label={`${questionNumber}) ${question}`}>
                <RadioGroup onChange={(_, data) => { onAnswer(id, data.value, answerType) }}>
                    {options?.map((o, i) => <Radio key={`questionOption${id}${i}`} label={o} value={o} />)}
                </RadioGroup>
            </Field>
        // rating question
        case SurveyAnswerType.Rating:
            return <Field label={`${questionNumber}) ${question}`}>
                <Rating color="brand" onChange={(_: any, data: any) => onAnswer(id, String(data.value), answerType)} />
            </Field>;
        // NPS scale 0-10
        case SurveyAnswerType.NPSScale:
            return <Field label={`${questionNumber}) ${question}`}>
                <RadioGroup onChange={(_, data) => { onAnswer(id, data.value, answerType); }}>
                    {Array.from({ length: 11 }).map((_, n) => (
                        <Radio key={`nps_${id}_${n}`} label={`${n}`} value={`${n}`} />
                    ))}
                </RadioGroup>
            </Field>;
        // multiple-choice (checkboxes)
        case SurveyAnswerType.MultipleValue:
            return <Field label={`${questionNumber}) ${question}`}>
                <MultipleChoice id={id} options={options} onAnswer={onAnswer} answerType={answerType} />
            </Field>;
    }
}

export default SurveyAnswerView;

// helper component for multiple-choice questions
interface MultipleChoiceProps {
    id: string;
    options?: string[];
    onAnswer: (id: string, answer: string, type: SurveyAnswerType) => void;
    answerType: SurveyAnswerType;
}

const MultipleChoice: FC<MultipleChoiceProps> = ({ id, options = [], onAnswer, answerType }: MultipleChoiceProps) => {
    const [selected, setSelected] = useState<Record<string, boolean>>({});

    const toggle = (opt: string) => {
        setSelected(s => {
            const next = { ...s, [opt]: !s[opt] };
            const selectedValues = Object.keys(next).filter(k => next[k]);
            // send the selected values as a JSON string
            onAnswer(id, JSON.stringify(selectedValues), answerType);
            return next;
        });
    };

    return <div>
        {options.map((o, i) => (
            <Checkbox key={`mc_${id}_${i}`} label={o} checked={!!selected[o]} onChange={() => toggle(o)} />
        ))}
    </div>;
};